import { defineStore } from 'pinia';
import { toRaw } from 'vue';
import type { SampleRoundDto } from '@/ApiClient';
import { entityCache } from '@/store/cache-primitives';

// Showing a cached detail always revalidates in the background, so stale entries
// self-heal on selection.
export const useSampleRoundDetails = defineStore('sample-round-detail-cache', () => {
  const entities = entityCache<SampleRoundDto>();
  const details = entities.data;

  // Snapshot persistence contract — see store/warm-boot.ts.
  const snapshotDto = 'SampleRoundDto' as const;
  const SNAPSHOT_VERSION = 1;

  type SampleRoundDetailsSnapshot = {
    version: number;
    details: Record<number, SampleRoundDto>;
  };

  function snapshotData(): SampleRoundDetailsSnapshot {
    // toRaw: IndexedDB's structured clone can't handle Vue's reactive proxies
    return { version: SNAPSHOT_VERSION, details: toRaw(details.value) };
  }

  function seedSnapshot(data: unknown) {
    const snapshot = data as Partial<SampleRoundDetailsSnapshot>;
    if (snapshot.version !== SNAPSHOT_VERSION) return;
    if (Object.keys(details.value).length > 0) return;
    entities.seed(snapshot.details ?? {});
  }

  return {
    details,
    snapshotDto,
    snapshotData,
    seedSnapshot,
    get: entities.get,
    set: entities.set,
    remove: entities.remove,
    clear: entities.clear,
  };
});
